import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Search, Loader2, X, ExternalLink, Printer, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Catalogo() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("all");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fitosanitari/list`, {
        params: { q, stato, page: pageNum, page_size: 30 },
        withCredentials: true,
      });
      setItems(r.data.items || []);
      setPage(r.data.page);
      setPages(r.data.pages);
      setTotal(r.data.total);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [q, stato]);

  useEffect(() => { const timer = setTimeout(() => load(1), 400); return () => clearTimeout(timer); }, [q, stato, load]);

  const printScheda = () => window.print();

  const cercaSDS = (nome) => {
    const query = `"${nome}" scheda sicurezza fitosanitario filetype:pdf`;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, "_blank");
  };

  const statusLabel = (label) => {
    const key = (label || "").toLowerCase();
    const map = {
      autorizzato: t("catalogo.status.authorized"),
      revocato: t("catalogo.status.revoked"),
      scaduto: t("catalogo.status.expired"),
      sospeso: t("catalogo.status.suspended"),
      sconosciuto: t("catalogo.status.unknown"),
    };
    return map[key] || label;
  };

  const stateBadge = (label) => {
    const m = {
      Autorizzato: { bg: "#e4f0e6", fg: "#1f5b2b", icon: <CheckCircle2 size={12} /> },
      Revocato: { bg: "#fde3df", fg: "#a3271a", icon: <AlertTriangle size={12} /> },
      Scaduto: { bg: "#fdecd6", fg: "#a35f00", icon: <Clock size={12} /> },
      Sospeso: { bg: "#fdf3d6", fg: "#7a5a00", icon: <Clock size={12} /> },
    };
    const c = m[label] || m.Revocato;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded"
        style={{ background: c.bg, color: c.fg, border: `1px solid ${c.fg}` }}>
        {c.icon} {statusLabel(label).toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen pb-24" data-testid="catalogo-page">
      <Header title={t("catalogo.headerTitle")} subtitle={t("catalogo.headerSubtitle")} />
      <main className="max-w-4xl mx-auto px-4 py-5 print-hide">
        <div className="vs-card p-4 mb-4">

        <div className="relative mb-3">
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("catalogo.searchPlaceholder")}
              className="vs-input w-full"
              style={{ paddingRight: 42 }}
              data-testid="search-input"
            />
            <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-muted)" }} />
          </div>

          <div className="flex gap-2 flex-wrap">
            {[["all",t("catalogo.filterAll")],["autorizzato",t("catalogo.filterAuthorized")],["revocato",t("catalogo.filterRevoked")],["scaduto",t("catalogo.filterExpired")]].map(([v,l])=>(
              <button key={v} onClick={()=>setStato(v)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{
                  background: stato===v?"var(--primary)":"var(--surface)",
                  color: stato===v?"white":"var(--text-primary)",
                  border:`2px solid ${stato===v?"var(--primary)":"var(--border)"}`,
                }}
                data-testid={`filter-${v}`}>{l}</button>
            ))}
          </div>
          <div className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
            {total > 0 ? t("catalogo.resultsCount", { count: total, formattedCount: total.toLocaleString(dateLocale) }) : (loading ? "..." : t("catalogo.noResults"))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <button key={it.num_registrazione} onClick={() => setSelected(it)}
                className="vs-card p-3 w-full text-left hover:shadow-md transition-shadow"
                style={{ borderLeft: `4px solid ${it.is_usabile?"var(--primary)":"var(--accent)"}` }}
                data-testid={`product-${it.num_registrazione}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="heading font-bold truncate">{it.denominazione_prodotto}</div>
                    {it.sostanze_attive && (
                      <div className="text-xs mt-0.5 truncate" style={{ color:"var(--text-muted)" }}>
                        {it.sostanze_attive}
                      </div>
                    )}
                    {it.ragione_sociale && (
                      <div className="text-[11px] mt-0.5 italic" style={{ color:"var(--text-muted)" }}>
                        {it.ragione_sociale}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0">{stateBadge(it.stato_label)}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-5">
            <button disabled={page<=1} onClick={()=>load(page-1)} className="vs-btn-secondary text-sm px-3 py-1.5 disabled:opacity-40">{t("catalogo.prevPageButton")}</button>
            <span className="text-sm py-1.5" style={{ color:"var(--text-muted)" }}>{t("catalogo.pageOf", { page, pages })}</span>
            <button disabled={page>=pages} onClick={()=>load(page+1)} className="vs-btn-secondary text-sm px-3 py-1.5 disabled:opacity-40">{t("catalogo.nextPageButton")}</button>
          </div>
        )}
      </main>

      {/* MODALE DETTAGLIO */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 print-hide"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="vs-card max-w-2xl w-full p-5 max-h-[92vh] overflow-y-auto" style={{ border: "2px solid var(--primary)" }} id="scheda-print">
            <div className="flex items-start justify-between mb-3 print-hide">
              <h3 className="heading text-xl font-extrabold">{t("catalogo.modalTitle")}</h3>
              <button onClick={()=>setSelected(null)} className="p-1"><X size={22} /></button>
            </div>

            <div className="text-center mb-4 pb-3" style={{ borderBottom: "2px solid var(--text-primary)" }}>
              <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("catalogo.modalSourceLabel")}</div>
              <div className="heading text-2xl font-extrabold mt-1">{selected.denominazione_prodotto}</div>
              <div className="mt-2">{stateBadge(selected.stato_label)}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Info label={t("catalogo.fields.registrationNumber")} value={selected.num_registrazione} />
              <Info label={t("catalogo.fields.company")} value={selected.ragione_sociale} />
              <Info label={t("catalogo.fields.activeIngredient")} value={selected.sostanze_attive} span />
              <Info label={t("catalogo.fields.category")} value={selected.attivita} />
              <Info label={t("catalogo.fields.formulation")} value={selected.descrizione_formulazione} />
              <Info label={t("catalogo.fields.registrationDate")} value={selected.data_registrazione} />
              <Info label={t("catalogo.fields.expiryDate")} value={selected.data_scadenza_autorizzazione} />
              <Info label={t("catalogo.fields.hazardStatements")} value={selected.indicazioni_di_pericolo} span />
              {!selected.is_usabile && (
                <>
                  <Info label={t("catalogo.fields.revocationReason")} value={selected.motivo_della_revoca} span />
                  <Info label={t("catalogo.fields.revocationDate")} value={selected.data_decorrenza_revoca} />
                </>
              )}
            </div>

            {!selected.is_usabile && (
              <div className="mt-4 p-3 rounded-lg" style={{ background: "#fde3df", border: "2px solid #a3271a" }}>
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} style={{ color: "#a3271a" }} className="shrink-0 mt-0.5" />
                  <div className="text-sm" style={{ color: "#a3271a" }}>
                    <strong>{t("catalogo.warningLabel")}</strong>: {t("catalogo.warningText")}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-5 print-hide">
              <button onClick={() => cercaSDS(selected.denominazione_prodotto)} className="vs-btn-primary flex-1 inline-flex items-center justify-center gap-2">
                <ExternalLink size={16} /> {t("catalogo.searchSdsButton")}
              </button>
              <button onClick={printScheda} className="vs-btn-secondary flex-1 inline-flex items-center justify-center gap-2">
                <Printer size={16} /> {t("catalogo.printButton")}
              </button>
            </div>
            <p className="text-[10px] mt-3 italic text-center print-hide" style={{ color: "var(--text-muted)" }}>
              {t("catalogo.sdsDisclaimer")}
            </p>
          </div>
        </div>
      )}

      <BottomNav />

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 15mm; }
          body * { visibility: hidden; }
          #scheda-print, #scheda-print * { visibility: visible; }
          #scheda-print { position: absolute; left: 0; top: 0; width: 100%; border: none !important; box-shadow: none !important; }
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}

function Info({ label, value, span }) {
  if (!value || value === "-") return null;
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}
